import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { supabase } from "@/integrations/supabase/client";
import { requestPasswordReset } from "@/lib/password-reset.functions";

export const Route = createFileRoute("/pozabljeno-geslo")({
  head: () => ({
    meta: [
      { title: "Pozabljeno geslo — kupish.si" },
      { name: "robots", content: "noindex,nofollow" },
    ],
    links: [{ rel: "canonical", href: "https://kupish.si/pozabljeno-geslo" }],
  }),
  component: Page,
});

function Page() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [sentTo, setSentTo] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const requestReset = useServerFn(requestPasswordReset);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    const normalized = email.trim().toLowerCase();
    if (!normalized || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalized)) {
      setError("Vnesite veljaven e-naslov.");
      return;
    }

    setLoading(true);
    // Odjavi morebitno obstoječo sejo, da nova povezava ne pristane v napačnem računu.
    try {
      await supabase.auth.signOut();
    } catch {
      // ignore
    }

    try {
      await requestReset({
        data: { email: normalized, origin: window.location.origin },
      });
      setSentTo(normalized);
      setSent(true);
    } catch (err) {
      setError((err as Error).message || "Napaka pri pošiljanju povezave.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <section className="mx-auto max-w-md px-6 py-24">
      <div className="eyebrow">Ponastavitev</div>
      <h1 className="font-serif text-5xl mt-3">Pozabljeno geslo</h1>
      <p className="mt-3 text-sm text-muted-foreground">
        Vpišite svoj e-mail in poslali vam bomo povezavo za nastavitev novega gesla.
      </p>
      {sent ? (
        <div className="mt-10 border border-border p-6 text-sm space-y-3">
          <p>
            Če obstaja račun za <strong>{sentTo}</strong>, smo na ta e-naslov poslali povezavo za ponastavitev gesla.
          </p>
          <p className="text-muted-foreground">
            Preverite tudi mapo z neželeno pošto. Povezava velja <strong>7 dni</strong>. Če e-pošte ne prejmete, preverite, ali ste vpisali isti e-naslov, s katerim ste se registrirali.
          </p>
        </div>
      ) : (
        <form className="mt-10 space-y-5" onSubmit={onSubmit}>
          <div>
            <label className="text-xs eyebrow">E-mail</label>
            <input
              type="email"
              required
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-2 w-full bg-background border border-border px-4 py-3 text-sm focus:outline-none focus:border-foreground"
            />
            <p className="mt-2 text-xs text-muted-foreground">
              Uporabite isti e-naslov, s katerim ste se registrirali. Povezava bo veljavna 7 dni.
            </p>
          </div>
          {error && <div className="text-sm text-destructive">{error}</div>}
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-primary text-primary-foreground py-3 text-sm hover:opacity-90 disabled:opacity-60"
          >
            {loading ? "Pošiljam…" : "Pošlji povezavo"}
          </button>
        </form>
      )}
      <div className="mt-6 text-sm text-center">
        <Link to="/prijava" className="underline underline-offset-4 hover:text-accent">
          Nazaj na prijavo
        </Link>
      </div>
    </section>
  );
}

